import { pathText } from './ast.js';
import { findFunction } from './registry.js';
import * as runtime from './runtime.js';
import { isRows, requireDecimal, toValue, truthy } from './values.js';
/**
 * The TypeScript runtime back-end (§7): a tree-walking interpreter over the runtime module — FormulaDecimal (28
 * significant digits, ties to even) with the null semantics of §4.4 in `runtime.ts`, the same functions the
 * compile-time back-end's emitted code calls. Runs on a resolved, checked tree.
 *
 * A collection symbol's input is its rows (`FormulaCollection`), each a row of inputs in the collection's own
 * scope; an aggregate evaluates its expression and `where:` once per row with a nested interpreter over that row.
 */
export function evaluate(root, inputs, options = { rounding: 'halfEven' }) {
    return new Interpreter(inputs, options).eval(root);
}
const LOGICAL_OPERATORS = new Set(['&&', '||']);
class Interpreter {
    constructor(inputs, options) {
        this.inputs = inputs;
        this.options = options;
    }
    eval(node) {
        switch (node.kind) {
            case 'num':
                return runtime.num(node.value);
            case 'str':
            case 'bool':
                return node.value;
            case 'null':
                return null;
            case 'ref':
                return toValue(this.input(node));
            case 'unary':
                return this.evalUnary(node);
            case 'binary':
                return this.evalBinary(node);
            case 'coalesce':
                return this.eval(node.left) ?? this.eval(node.right);
            case 'cond':
                return truthy(this.eval(node.test)) ? this.eval(node.then) : this.eval(node.else);
            case 'call':
                return this.evalCall(node);
        }
    }
    input(node) {
        if (node.symbol === null)
            throw new Error(`'${pathText(node)}' is unresolved; evaluate a resolved tree.`);
        return this.inputs(node.symbol);
    }
    evalUnary(node) {
        const operand = this.eval(node.operand);
        if (node.op === '!')
            return !truthy(operand);
        return operand === null ? null : runtime.neg(requireDecimal(operand, '-'));
    }
    evalBinary(node) {
        if (LOGICAL_OPERATORS.has(node.op))
            return this.evalLogical(node);
        const left = this.eval(node.left);
        const right = this.eval(node.right);
        if (left === null || right === null)
            return null;
        switch (node.op) {
            case '+':
                return runtime.add(requireDecimal(left, node.op), requireDecimal(right, node.op));
            case '-':
                return runtime.sub(requireDecimal(left, node.op), requireDecimal(right, node.op));
            case '*':
                return runtime.mul(requireDecimal(left, node.op), requireDecimal(right, node.op));
            case '/':
                return runtime.div(requireDecimal(left, node.op), requireDecimal(right, node.op));
            default:
                return runtime.compare(node.op, left, right);
        }
    }
    /** `&&` and `||` evaluate the right side only when the left does not decide (§4.4); a null counts as false. */
    evalLogical(node) {
        const left = truthy(this.eval(node.left));
        if (node.op === '&&' ? !left : left)
            return left;
        return truthy(this.eval(node.right));
    }
    evalCall(node) {
        const fn = findFunction(node.fn);
        if (!fn)
            throw new Error(`'${node.fn}' is not a registered function; evaluate a checked tree.`);
        const rows = this.rowsOf(fn, node);
        if (rows !== undefined) {
            return fn.aggregate.evaluate({
                call: node,
                rows,
                options: this.options,
                evaluate: (body, row) => new Interpreter(row, this.options).eval(body),
            });
        }
        if (!fn.row)
            throw new Error(`'${node.fn}()' aggregates a collection, but '${pathText(node.args[0])}' was not supplied as rows.`);
        return fn.row.evaluate({
            call: node,
            arg: (index) => this.eval(node.args[index]),
            options: this.options,
        });
    }
    /**
     * The rows of the collection a call aggregates: the first argument's input when the function has a collection
     * form and the host supplied rows for it. A checked tree names a collection exactly where it aggregates, so the
     * input's shape decides which form runs — the host supplies rows for a collection symbol and nothing else.
     */
    rowsOf(fn, node) {
        const first = node.args[0];
        if (!fn.aggregate || first?.kind !== 'ref')
            return undefined;
        const input = this.input(first);
        return isRows(input) ? input : undefined;
    }
}
